'use no memo';
/**
 * Invalid Grant Handler Hook
 *
 * Handles invalid_grant errors from Logto SDK by attempting silent re-authentication.
 * Falls back to full login redirect if silent auth fails.
 *
 * AIDEV-NOTE: 'use no memo' directive - This hook intentionally mutates
 * sessionStateRef.current for synchronous auth state access during async flows.
 * This prevents race conditions when multiple API calls fail simultaneously.
 * The React Compiler cannot optimize this pattern.
 */

import { useCallback } from 'react';
import type { LogtoSignInOptions } from '@lib/logto';
import { Prompt } from '@logto/browser';
import { isNativePlatform } from '@lib/platform';
import type { QueryClient } from '@tanstack/react-query';
import { setGlobalSessionExpired } from '@api-client/api-client';
import {
  getAuthCallbackUrl,
  storeRedirectPath,
  silentReauthSlot,
  isUserCancellation,
} from '@utils/auth';
import {
  getRecoveryFlowRecord,
  getRecoveryRecord,
  getRecoveryRecordAsync,
  updateRecoveryRecord,
  updateRecoveryRecordAsync,
  isRecoveryTerminal,
  type RecoveryFlowKind,
} from '@utils/authRecovery';
import { logger } from '@utils/logger';
import { sleep } from '@utils/sleep';
import { clearAuthState } from './clearAuthState';
import { AuthSessionState, AuthAction, LogtoExtendedError } from './authTypes';

// =============================================================================
// ERROR DETECTION HELPERS
// =============================================================================

/**
 * AIDEV-NOTE: Checks if error is an invalid_grant from Logto
 * This only happens when the refresh token itself has expired or was revoked
 *
 * Shape notes for future Logto upgrades:
 * - Direct `code` / `error` fields were observed in Meetball Sentry while
 *   running `@logto/react@4.0.9` with `@logto/browser@3.0.11`; Logto's docs
 *   and older local repros still surface `oidc.invalid_grant` on `code`.
 * - Nested `cause` / `data` wrappers were also observed in Meetball Sentry on
 *   `@logto/react@4.0.9` with `@logto/browser@3.0.11`.
 * - `LogtoRequestError` has been explicitly exported since the upstream JS SDK
 *   `f9ff6f4` changeset that shipped in `@logto/react@2.1.2` (paired with
 *   `@logto/browser@2.1.2` in that release train), so keep a name-based
 *   fallback for wrapper-only variants even if the nested fields move again.
 */
export const isInvalidGrantError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const logtoError = error as LogtoExtendedError;
  const isInvalidGrantCode = (value?: string) =>
    value === 'oidc.invalid_grant' || value === 'invalid_grant';

  // Check direct properties (standard error shape)
  if (isInvalidGrantCode(logtoError.code) || logtoError.error === 'invalid_grant') {
    return true;
  }

  // Check nested cause (LogtoRequestError wraps the actual error)
  if (logtoError.cause) {
    if (isInvalidGrantCode(logtoError.cause.code) || logtoError.cause.error === 'invalid_grant') {
      return true;
    }
  }

  // Check nested data property (some SDK versions use this)
  if (logtoError.data) {
    if (isInvalidGrantCode(logtoError.data.code) || logtoError.data.error === 'invalid_grant') {
      return true;
    }
  }

  // Check error message as fallback
  if (
    logtoError.message?.includes('invalid_grant') ||
    logtoError.message?.toLowerCase().includes('grant request is invalid')
  ) {
    return true;
  }

  // Check if it's a LogtoRequestError by name
  if (logtoError.name === 'LogtoRequestError') {
    // Log the full error structure to help debug
    logger.warn('LogtoRequestError detected, checking structure:', {
      code: logtoError.code,
      error: logtoError.error,
      message: logtoError.message,
      cause: logtoError.cause,
      data: logtoError.data,
    });
    // AIDEV-NOTE: Only match specific invalid_grant patterns, NOT just any message with "grant"
    // The overly broad check was causing false positives
    const msg = logtoError.message?.toLowerCase() ?? '';
    if (
      msg.includes('invalid_grant') ||
      msg.includes('grant request is invalid') ||
      msg.includes('refresh token not found') ||
      msg.includes('grant not found')
    ) {
      return true;
    }
  }

  return false;
};

/**
 * AIDEV-NOTE: Checks if error is "not authenticated" - expected during transitions
 * These are not errors that need handling, just indicates user needs to authenticate
 */
export const isNotAuthenticatedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const logtoError = error as LogtoExtendedError;
  return (
    logtoError.message?.includes('Not authenticated') ||
    (logtoError.name === 'LogtoClientError' && logtoError.code === 'auth.unauthorized')
  );
};

// =============================================================================
// HOOK OPTIONS
// =============================================================================

export interface UseInvalidGrantHandlerOptions {
  /** Ref to current session state for synchronous access */
  sessionStateRef: React.RefObject<AuthSessionState>;
  /** Dispatch function for session state updates */
  dispatch: React.Dispatch<AuthAction>;
  /** Logto SDK clearAllTokens function */
  clearAllTokens: () => Promise<void>;
  /** Logto SDK signIn as AuthProvider wraps it: clears the "Signed in as ..."
   * cue before starting the sign-in (see AuthProvider's `logtoSignIn`). */
  logtoSignIn: (options: LogtoSignInOptions) => Promise<void>;
  /**
   * Live ref to the Logto adapter's `isAuthenticated` state.
   *
   * The native (Capacitor) adapter swallows user-cancel of the system browser
   * and resolves `signIn` normally (see NativeLogtoAdapterProvider.tsx
   * `isUserCancellation` branch). A plain `await logtoSignIn()` cannot
   * distinguish successful re-auth from a silently-swallowed cancel.
   * `runNativeAttempt` reads this ref after the awaited signIn + one render
   * tick to verify auth actually happened; if not, the flow falls through to
   * the terminal-drawer path instead of leaving the app stuck in
   * REAUTHENTICATING with the interceptor blocking every request.
   */
  logtoIsAuthenticatedRef: React.RefObject<boolean>;
  /** TanStack Query client for cache clearing */
  queryClient: QueryClient;
  /** Zustand store setAuthenticated */
  setAuthenticated: (isAuthenticated: boolean) => void;
  /** Zustand store setCurrentUser */
  setCurrentUser: (user: null) => void;
  /** Clear user from React Query cache */
  clearUser: () => void;
  /**
   * Called when recovery reaches a terminal state (all attempts exhausted,
   * user cancelled system browser, or existing record was already terminal).
   * AuthProvider uses this to open the SessionExpiredDrawer. Issue #567.
   */
  onSessionExpired?: () => void;
}

export interface UseInvalidGrantHandlerReturn {
  /** Handle invalid_grant error with silent re-auth attempt */
  handleInvalidGrant: (error: unknown) => Promise<void>;
  /** Check if error is an invalid_grant error */
  isInvalidGrantError: (error: unknown) => boolean;
  /** Check if error is "not authenticated" (expected during transitions) */
  isNotAuthenticatedError: (error: unknown) => boolean;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

export function useInvalidGrantHandler(
  options: UseInvalidGrantHandlerOptions,
): UseInvalidGrantHandlerReturn {
  const {
    sessionStateRef,
    dispatch,
    clearAllTokens,
    logtoSignIn,
    logtoIsAuthenticatedRef,
    queryClient,
    setAuthenticated,
    setCurrentUser,
    clearUser,
    onSessionExpired,
  } = options;

  const handleInvalidGrant = useCallback(
    async (error: unknown) => {
      // AIDEV-NOTE: Issue #567 - Early exit if we're already handling an
      // invalid_grant. Without this, concurrent failing requests would each
      // trigger a full recovery cycle.
      const currentSessionState = sessionStateRef.current;
      if (
        currentSessionState === AuthSessionState.SESSION_EXPIRED ||
        currentSessionState === AuthSessionState.REAUTHENTICATING
      ) {
        logger.debug(
          'Session already expired or reauthenticating, skipping duplicate handleInvalidGrant',
        );
        return;
      }

      // Block all token requests immediately by flipping the global flag so
      // the request interceptor rejects pending/new API requests client-side.
      setGlobalSessionExpired(true);
      sessionStateRef.current = AuthSessionState.REAUTHENTICATING;
      dispatch({ type: 'SET_REAUTHENTICATING' });

      logger.warn('Session expired (invalid_grant) - starting recovery', error);

      // Store current location for post-login redirect.
      storeRedirectPath(
        globalThis.location.pathname,
        globalThis.location.search,
        globalThis.location.hash,
      );

      // Issue #567: Read the coordination record (sessionStorage + Capacitor
      // Preferences on native). The callback-path recovery in useSessionRecovery
      // writes to the same record, so attempts accumulate across recovery paths.
      const nativeMode = isNativePlatform();
      const rawExistingRecord = nativeMode ? await getRecoveryRecordAsync() : getRecoveryRecord();

      // Terminal (explicit or attempt cap reached): skip straight to drawer.
      // Pass `rawExistingRecord` explicitly — the default `isRecoveryTerminal()`
      // reads from sessionStorage only, which on native after a WebView kill
      // is empty even when Capacitor Preferences still holds a terminal
      // record. Omitting the arg here would make the native post-kill path
      // rerun recovery from scratch and reintroduce the cascade/redirect loop
      // this guard exists to prevent.
      if (isRecoveryTerminal(rawExistingRecord)) {
        logger.info('Recovery record is terminal - skipping attempts, showing drawer');
        await goTerminal({
          queryClient,
          setAuthenticated,
          setCurrentUser,
          clearUser,
          clearAllTokens,
          sessionStateRef,
          dispatch,
          onSessionExpired,
        });
        return;
      }

      // `breaker-tripped` is the out-of-band signal channel for the
      // token-refresh circuit breaker. It is NOT a stage in the recovery flow
      // — a real `invalid_grant` arriving while the breaker is tripped should
      // run a clean silent → full → terminal cycle. `getRecoveryFlowRecord`
      // is the single-source-of-truth filter (shared with `computeNextRecord`)
      // that strips the breaker signal so `existingKind` (used by
      // `runWebAttempts` to gate silent vs full) and the cycle-start guard
      // behave as if no record exists. Without this filter, `runWebAttempts`
      // skips silent re-auth (its gate is `currentKind === null ||
      // currentKind === 'fresh'`) and falls straight through to `goTerminal`,
      // recreating the very poisoning bug this whole change exists to fix.
      const existingRecord = getRecoveryFlowRecord(rawExistingRecord);

      // Explicit annotation forces the runtime `breaker-tripped` filter above
      // to be type-correct: if a future refactor removes the filter, this
      // assignment becomes a TypeScript error rather than a silent return of
      // the bug. `runWebAttempts` / `runNativeAttempt` agree to accept only
      // `RecoveryFlowKind` so the invariant is enforced end-to-end.
      const existingKind: RecoveryFlowKind | null = existingRecord?.kind ?? null;
      // Start a cycle if none exists (startedAt set by updateRecoveryRecord).
      if (!existingRecord) {
        updateRecoveryRecord({ type: 'START_CYCLE' });
      }

      // Issue #567: Shared cleanup with cancelQueries() BEFORE clear().
      await clearAuthState({
        queryClient,
        setAuthenticated,
        setCurrentUser,
        clearUser,
        clearAllTokens,
      });

      // Small delay so the React reducer dispatch above has settled before we
      // hand off to logtoSignIn, which will kick off a redirect.
      await sleep(50);

      const callbackUrl = getAuthCallbackUrl();

      if (nativeMode) {
        // Native: no silent auth (system browser can't do prompt=none).
        // A single full-login attempt, then terminal.
        await runNativeAttempt({
          logtoSignIn,
          logtoIsAuthenticatedRef,
          callbackUrl,
          existingKind,
          sessionStateRef,
          dispatch,
          queryClient,
          setAuthenticated,
          setCurrentUser,
          clearUser,
          clearAllTokens,
          onSessionExpired,
        });
        return;
      }

      // Web: silent first (cheap, no UI), escalate to full once, then terminal.
      await runWebAttempts({
        logtoSignIn,
        logtoIsAuthenticatedRef,
        callbackUrl,
        existingKind,
        sessionStateRef,
        dispatch,
        queryClient,
        setAuthenticated,
        setCurrentUser,
        clearUser,
        clearAllTokens,
        onSessionExpired,
      });
    },
    [
      clearAllTokens,
      clearUser,
      dispatch,
      logtoSignIn,
      logtoIsAuthenticatedRef,
      onSessionExpired,
      queryClient,
      sessionStateRef,
      setAuthenticated,
      setCurrentUser,
    ],
  );

  return {
    handleInvalidGrant,
    isInvalidGrantError,
    isNotAuthenticatedError,
  };
}

// =============================================================================
// INTERNAL: TERMINAL / WEB / NATIVE FLOWS
// =============================================================================

interface TerminalFlowArgs {
  queryClient: QueryClient;
  setAuthenticated: (v: boolean) => void;
  setCurrentUser: (u: null) => void;
  clearUser: () => void;
  clearAllTokens: () => Promise<void>;
  sessionStateRef: React.RefObject<AuthSessionState>;
  dispatch: React.Dispatch<AuthAction>;
  onSessionExpired?: () => void;
}

// Issue #567 follow-up: drawer opens in-place on the user's current route.
// The redirect path captured earlier in handleInvalidGrant lets "Sign in
// again" restore the original route via useCallbackRedirect; "Continue
// as guest" in AuthProvider handles the safe /home fallback.
async function goTerminal(args: TerminalFlowArgs): Promise<void> {
  // Mark terminal and clean up local state so the UI is consistent.
  updateRecoveryRecord({ type: 'TERMINAL', reason: 'explicit' });
  await clearAuthState({
    queryClient: args.queryClient,
    setAuthenticated: args.setAuthenticated,
    setCurrentUser: args.setCurrentUser,
    clearUser: args.clearUser,
    clearAllTokens: args.clearAllTokens,
  });
  args.sessionStateRef.current = AuthSessionState.SESSION_EXPIRED;
  args.dispatch({ type: 'SET_SESSION_EXPIRED' });
  try {
    args.onSessionExpired?.();
  } catch (err) {
    // Session state is already SESSION_EXPIRED. If the callback that opens the
    // drawer threw (e.g., a setter from a torn-down provider), the user would
    // be stranded on a protected route with no recovery UI. Force-navigate to
    // /hi as a hard fallback so they can re-enter the auth flow manually.
    logger.error('[auth-drawer-fail] onSessionExpired callback threw:', err);
    if (typeof window !== 'undefined') {
      window.location.href = '/hi';
    }
  }
}

interface AttemptArgs {
  logtoSignIn: (options: LogtoSignInOptions) => Promise<void>;
  logtoIsAuthenticatedRef: React.RefObject<boolean>;
  callbackUrl: string;
  existingKind: RecoveryFlowKind | null;
  sessionStateRef: React.RefObject<AuthSessionState>;
  dispatch: React.Dispatch<AuthAction>;
  queryClient: QueryClient;
  setAuthenticated: (v: boolean) => void;
  setCurrentUser: (u: null) => void;
  clearUser: () => void;
  clearAllTokens: () => Promise<void>;
  onSessionExpired?: () => void;
}

async function runWebAttempts(args: AttemptArgs): Promise<void> {
  let currentKind = args.existingKind;

  // Attempt 1: silent via prompt=none.
  if (currentKind === null || currentKind === 'fresh') {
    updateRecoveryRecord({ type: 'SILENT_ATTEMPTED' });
    currentKind = 'silent-attempted';
    silentReauthSlot.write(true);
    try {
      logger.info('Attempting silent re-auth with prompt=none');
      await args.logtoSignIn({ redirectUri: args.callbackUrl, prompt: Prompt.None });
      // If this resolves, the SDK has kicked off a redirect. Flow continues on /callback.
      return;
    } catch (silentErr) {
      logger.info('Silent re-auth failed - escalating to full login', silentErr);
    }
  }

  // Attempt 2: single full login.
  if (currentKind === 'silent-attempted') {
    // The `/callback` this redirect returns to is an interactive sign-in, not the
    // silent leg: drop the marker, or a return within its TTL would be painted and
    // routed as silent and skip the post-login steps (profile check, claim, intent).
    // Cleared here rather than in the catch above so a re-entry that resumes at
    // 'silent-attempted' drops a marker left by the earlier silent attempt too.
    silentReauthSlot.clear();
    // Keep the record sub-terminal across the interactive redirect. If the
    // callback returns with error=invalid_grant, useSessionRecovery needs a
    // non-terminal record to run clearLogtoSessionAndRetry(), which hits
    // /oidc/session/end to clear a corrupted Logto server-side session cookie
    // before the last retry is exhausted. Writing a terminal record here would
    // skip that cleanup and let users bounce straight back into the same
    // invalid_grant loop. If the full login throws in-app (user never leaves),
    // goTerminal() below writes the terminal record explicitly.
    updateRecoveryRecord({ type: 'FULL_INITIATED' });
    args.sessionStateRef.current = AuthSessionState.SESSION_EXPIRED;
    args.dispatch({ type: 'SET_SESSION_EXPIRED' });
    try {
      logger.info('Attempting full login redirect');
      await args.logtoSignIn({ redirectUri: args.callbackUrl });
      return;
    } catch (fullErr) {
      logger.error('Full login redirect failed:', fullErr);
    }
  }

  if (currentKind === 'full-initiated') {
    // Re-entry after a full-login redirect already ran (e.g. WebView restored
    // to a stale invalid_grant state). Silent and full gates are both skipped
    // - log why we're going straight to the drawer so this isn't invisible.
    logger.info(
      'runWebAttempts: re-entered with full-initiated record; attempts exhausted, going terminal',
    );
  }

  // All attempts exhausted.
  await goTerminal({
    queryClient: args.queryClient,
    setAuthenticated: args.setAuthenticated,
    setCurrentUser: args.setCurrentUser,
    clearUser: args.clearUser,
    clearAllTokens: args.clearAllTokens,
    sessionStateRef: args.sessionStateRef,
    dispatch: args.dispatch,
    onSessionExpired: args.onSessionExpired,
  });
}

async function runNativeAttempt(args: AttemptArgs): Promise<void> {
  // Native: no silent auth. Single full-login attempt via system browser.
  // On success, useNativePostAuth handles the auth transition and clears the record.
  if (args.existingKind !== 'terminal') {
    // Await the Capacitor Preferences mirror before handing control to the
    // system browser. `updateRecoveryRecord()` on native is fire-and-forget;
    // if the WebView gets backgrounded or killed during the OAuth round-trip
    // before the write lands, the record exists only in sessionStorage (which
    // dies with the WebView), defeating the whole point of persisting it
    // across WebView kills.
    await updateRecoveryRecordAsync({ type: 'FULL_INITIATED' });
    try {
      logger.info('Native invalid_grant: opening system browser for re-auth');
      await args.logtoSignIn({ redirectUri: args.callbackUrl });

      // The Capacitor adapter swallows user cancel of the system browser and
      // resolves signIn normally (so other callers can differentiate cancel
      // from real errors). Resolving does NOT mean we authenticated. Yield one
      // task so React flushes the adapter's post-signIn
      // `setIsAuthenticated(true)` into the ref, then verify auth actually
      // happened. Without this, cancellation leaves the app stuck in
      // REAUTHENTICATING with the request interceptor hard-rejecting
      // everything and auth navigation suppressed.
      await new Promise(resolve => setTimeout(resolve, 0));
      if (args.logtoIsAuthenticatedRef.current) {
        return;
      }
      logger.info(
        'Native re-auth: signIn resolved without authentication (likely user cancel) - going terminal',
      );
    } catch (reAuthError: unknown) {
      if (isUserCancellation(reAuthError)) {
        logger.info('Native re-auth: user cancelled system browser');
      } else {
        logger.error('Native re-auth failed:', reAuthError);
      }
    }
  }

  // Terminal: show drawer, stay on current route.
  await goTerminal({
    queryClient: args.queryClient,
    setAuthenticated: args.setAuthenticated,
    setCurrentUser: args.setCurrentUser,
    clearUser: args.clearUser,
    clearAllTokens: args.clearAllTokens,
    sessionStateRef: args.sessionStateRef,
    dispatch: args.dispatch,
    onSessionExpired: args.onSessionExpired,
  });
}
